import { c as _c } from "react/compiler-runtime";
import { Button as AntButton } from 'antd';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashAlt, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { faLongArrowLeft, faLongArrowRight } from '@commutifi-fe/custom-icons/regular';
import { faEye } from '@commutifi-fe/custom-icons/solid';
import { ButtonGroupProps, ButtonHTMLType, ButtonProps, ButtonShape, ButtonSize, ButtonType, ButtonColorType } from 'antd/es/button';
import { Overwrite } from 'utility-types';
import cx from 'classnames';
import React from 'react';
import './styles.scss';
import { useLocaleReceiver } from '../locales/LocaleReceiver';
import { spacing } from '../style/constants';
import { pickBy } from 'lodash';

/**
 * Filters out undefined values from an object.
 * This prevents undefined props from overriding default values when spreading.
 */
const filterUndefined = <T extends Record<string, unknown>,>(obj: T): Partial<T> => pickBy(obj, value => value !== undefined) as Partial<T>;
const {
  Group
} = AntButton;
export type { ButtonGroupProps, ButtonHTMLType, ButtonShape, ButtonSize, ButtonType };
export enum CommutifiButtonPreset {
  Edit = 'edit',
  Delete = 'delete',
  View = 'view',
  Caret = 'caret',
  Back = 'back',
  GoTo = 'goTo',
  Add = 'add',
}
type CustomButtonSize = 'small-compact';
type CustomButtonSizeType = ButtonSize | CustomButtonSize;
type CustomButtonColor = 'white' | 'off-white';
type CustomButtonColorType = ButtonColorType | CustomButtonColor;
export interface AppButtonProps extends Overwrite<ButtonProps, {
  type?: ButtonType | 'text-secondary';
  size?: CustomButtonSizeType;
  color?: CustomButtonColorType;
}> {
  /**
   * Icon to display after the button text.
   */
  suffixIcon?: React.ReactNode;
  /**
   * Shortcut notation to use pre-built specific buttons
   * like CaretButton, DeleteButton, EditButton, etc.
   */
  preset?: `${CommutifiButtonPreset}`;
  /**
   * Use this props ONLY on text or link buttons to make the button
   * height and width fit to the content instead of having a defined height and padding.
   */
  fitToContent?: boolean;
  underline?: boolean;
  /**
   * Use this props ONLY on text or link buttons to add horizontal padding on hover.
   * This improves visual alignment with surrounding text elements since text/link
   * buttons have no visible borders when not hovered.
   */
  dynamicHorizontalPadding?: boolean;
}
const customSizeToAntdSize: Record<CustomButtonSize, ButtonSize> = {
  'small-compact': 'small'
};
const isAntdButtonSize = (size: CustomButtonSizeType): size is ButtonSize => Boolean(size && ['small', 'middle', 'large'].includes(size));
const getAntdButtonSize = (size: CustomButtonSizeType): ButtonSize => isAntdButtonSize(size) ? size : customSizeToAntdSize[size] || 'middle';
const isAntdButtonColor = (color: CustomButtonColorType | undefined): color is ButtonColorType => Boolean(color && ['default', 'primary', 'danger'].includes(color));
const getAntdButtonColor = (color: CustomButtonColorType | undefined): ButtonColorType | undefined => isAntdButtonColor(color) ? color : undefined;
const customSizeToClassName: Record<CustomButtonSize, string> = {
  'small-compact': 'app-button-small--compact'
};
export interface ButtonComponent {
  (props: AppButtonProps & {
    ref?: React.Ref<HTMLButtonElement>;
  }): React.ReactElement | null;
  Group: typeof Group;
  Classes: {
    floatOnMobile: string;
  };
  displayName?: string;
}
const ButtonClass = 'app-button';

/**
 * This class is a wrap around [antd Button](https://ant.design/components/button/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 * IMPORTANT Style guide to antd property mapping (antd - commutifi style):
 *      | antd |    Figma styleguide |
 *    type
 *      - primary : Primary
 *      - primary + ghost=true  : Secondary
 *      - default : Tertiary
 *      - text : Same than antd but without padding
 *      - link : Same than antd but without padding
 *      - secondary : Ghost
 *    size
 *      - large : onboarding flow + future cases ?
 *      - middle (new default value) : medium
 *      - small : compact
 * ------
 *https://typescript-eslint.io/rules/no-shadow
 * @example Use icon prop to display an icon BEFORE the button text
 * @example Use children to have the flexibility to put the icon AFTER the text
 *
 * @example Use type='link' to use button links (You might need to adjust padding)
 */
/**
 * Builds the shared props object for the underlying AntButton (or preset component).
 * Extracted so both the AntButton path and preset path share the same prop logic.
 */
function buildButtonProps({
  className,
  icon,
  suffixIcon,
  fitToContent,
  dynamicHorizontalPadding,
  underline,
  type,
  size,
  color,
  children,
  ...buttonProps
}: Omit<AppButtonProps, 'preset'>) {
  const finalSize = getAntdButtonSize(size);
  const finalColor = getAntdButtonColor(color);
  const sharedProps = {
    size: finalSize,
    icon: React.isValidElement(icon) ? <span className="anticon ant-btn-icon" style={{
      display: 'inline-flex'
    }}>
        {icon}
      </span> : icon,
    color: finalColor,
    ...buttonProps,
    type: (type === 'text-secondary' ? 'text' : type) as ButtonType | undefined,
    className: cx(ButtonClass, underline && `${ButtonClass}--underline`, fitToContent && `${ButtonClass}--fit-to-content`, dynamicHorizontalPadding && `${ButtonClass}--dynamic-horizontal-padding`, type === 'text-secondary' && `${ButtonClass}--text-secondary`, size && !isAntdButtonSize(size) && customSizeToClassName[size], color && !isAntdButtonColor(color) && `${ButtonClass}-color--${color}`, className)
  };

  // Avoid wrapping in a Fragment when there is no suffixIcon — antd Button uses
  // React.Children internally and a Fragment wrapper changes how it processes
  // children (e.g. span wrapping, spacing insertion).
  const renderedChildren = suffixIcon ? <>
      {children}
      <span className="u-margin-left--small">{suffixIcon}</span>
    </> : children;
  return {
    sharedProps,
    renderedChildren
  };
}

// eslint-disable-next-line no-restricted-syntax -- Antd compound component pattern: generic forwardRef + static properties require type assertion
export const Button = (({
  preset,
  ref,
  ...rest
}: AppButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  // Preset components are rendered directly to avoid dynamic component
  // selection during render (react-hooks/static-components).
  // Preset components handle their own icon, children, and styling defaults,
  // so we pass through the remaining props and let each preset decide what to use.

  if (preset !== undefined) {
    // Compute custom CSS classes from original values before normalizing for antd.
    const {
      type,
      size,
      color,
      className,
      ...presetRest
    } = rest;
    const customClassName = cx(ButtonClass, type === 'text-secondary' && `${ButtonClass}--text-secondary`, size && !isAntdButtonSize(size) && customSizeToClassName[size], color && !isAntdButtonColor(color) && `${ButtonClass}-color--${color}`, className);
    const normalizedType = type === 'text-secondary' ? 'text' as const : type;
    const normalizedSize = size ? getAntdButtonSize(size) : size;
    const normalizedColor = color ? getAntdButtonColor(color) : color;
    const presetProps = {
      ...presetRest,
      className: customClassName,
      type: normalizedType,
      size: normalizedSize,
      color: normalizedColor
    };
    switch (preset as CommutifiButtonPreset) {
      case CommutifiButtonPreset.Back:
        return <BackButton ref={ref} {...presetProps} />;
      case CommutifiButtonPreset.Caret:
        return <CaretButton ref={ref} {...presetProps} />;
      case CommutifiButtonPreset.Delete:
        return <DeleteButton ref={ref} {...presetProps} />;
      case CommutifiButtonPreset.Edit:
        return <EditButton ref={ref} {...presetProps} />;
      case CommutifiButtonPreset.GoTo:
        return <GoToButton ref={ref} {...presetProps} />;
      case CommutifiButtonPreset.View:
        return <ViewButton ref={ref} {...presetProps} />;
      case CommutifiButtonPreset.Add:
        return <PlusButton ref={ref} {...presetProps} />;
    }
  }
  const {
    sharedProps,
    renderedChildren
  } = buildButtonProps(rest);
  return <AntButton ref={ref} {...sharedProps}>
      {renderedChildren}
    </AntButton>;
}) as unknown as ButtonComponent;
Button.displayName = 'Button';
Button.Group = Group;
Button.Classes = {
  /**
   * To make the button 'float' on bottom of the screen
   * when it's mobile display.
   */
  floatOnMobile: `${ButtonClass}--float`
};
export default Button;

/**
 * Following components are Button presets that you can use with the common Button
 * component passing preset property.
 * NOTE: They are in the same file to avoid circular dependencies
 */

export function LinkButton(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    const {
      className,
      style,
      ref,
      ...props
    } = t0;
    let t2;
    if ($[2] !== style) {
      t2 = {
        padding: 0,
        ...style
      };
      $[2] = style;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = <Button ref={ref} size="small" type="link" className={className} style={t2} {...filterUndefined(props)} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
interface EditButtonProps extends ButtonProps {
  onEdit?: (e: React.MouseEvent) => void;
  className?: string;
  text?: React.ReactNode;
  style?: React.CSSProperties;
}
export function EditButton(t0) {
  const $ = _c(16);
  const {
    onEdit,
    onClick,
    className,
    text,
    style,
    type,
    ref,
    ...btnProps
  } = t0;
  const intl = useLocaleReceiver("Button");
  const T0 = Button;
  const t1 = onEdit || onClick;
  const t2 = type || "link";
  const t3 = !text && "u-padding-horizontal--small";
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cx("app-edit-button", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = "edit-button";
  const t6 = filterUndefined(btnProps);
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={faPen} size="sm" />;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== intl || $[5] !== text) {
    t8 = text ?? intl.formatMessage({
      id: "editVariantText"
    });
    $[4] = intl;
    $[5] = text;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== T0 || $[8] !== ref || $[9] !== style || $[10] !== t1 || $[11] !== t2 || $[12] !== t4 || $[13] !== t6 || $[14] !== t8) {
    t9 = <T0 ref={ref} onClick={t1} type={t2} style={style} className={t4} data-testid={t5} {...t6} icon={t7}>{t8}</T0>;
    $[7] = T0;
    $[8] = ref;
    $[9] = style;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
interface DeleteButtonProps extends AppButtonProps {
  // Support legacy property to handle button click
  onDelete?: React.MouseEventHandler;
  showIcon?: boolean;
}
export function DeleteButton(t0) {
  const $ = _c(16);
  const {
    onDelete,
    onClick,
    className,
    children,
    showIcon: t1,
    ref,
    ...btnProps
  } = t0;
  const showIcon = t1 === undefined ? true : t1;
  const intl = useLocaleReceiver("Button");
  const T0 = Button;
  const t2 = btnProps.type !== "link" && btnProps.type !== "text";
  const t3 = onDelete || onClick;
  let t4;
  if ($[0] !== className) {
    t4 = cx("u-font-weight-semi", className);
    $[0] = className;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = filterUndefined(btnProps);
  let t6;
  if ($[2] !== showIcon) {
    t6 = showIcon ? <Icon icon={faTrashAlt} /> : undefined;
    $[2] = showIcon;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== children || $[5] !== intl) {
    t7 = children || intl.formatMessage({
      id: "deleteVariantText"
    });
    $[4] = children;
    $[5] = intl;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== T0 || $[8] !== ref || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <T0 ref={ref} ghost={t2} onClick={t3} className={t4} {...t5} icon={t6}>{t7}</T0>;
    $[7] = T0;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
interface TextButtonProps {
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  icon?: React.ReactNode;
  text: React.ReactNode;
  suffix?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Legacy, only used in ViewButton (button with eye icon)
 */
export function TextButton(t0) {
  const $ = _c(16);
  const {
    onClick,
    icon,
    suffix,
    text,
    className,
    style,
    ref
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = e => {
      e.preventDefault();
      if (onClick) {
        onClick(e as React.MouseEvent<HTMLAnchorElement>);
      }
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleOnClick = t1;
  let t2;
  if ($[2] !== style) {
    t2 = {
      padding: 0,
      ...style
    };
    $[2] = style;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== icon) {
    t3 = icon ? <span className="u-margin-right--tiny">{icon}</span> : null;
    $[4] = icon;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== suffix) {
    t4 = suffix ? <span className="u-margin-left--tiny">{suffix}</span> : null;
    $[6] = suffix;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== className || $[9] !== handleOnClick || $[10] !== ref || $[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== text) {
    t5 = <Button ref={ref} onClick={handleOnClick} type="link" style={t2} className={className}>{t3}{text}{t4}</Button>;
    $[8] = className;
    $[9] = handleOnClick;
    $[10] = ref;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = text;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
interface ViewButtonProps {
  onClick?: AppButtonProps['onClick'];
}

/**
 * Legacy, only used in detailed view of organization dashboard Table views.
 * We should use our app UI components instead
 *
 * Text: View
 * Icon: Eye
 * @defaultValue type='text' (Dark grey - neutral-500)
 */
export function ViewButton(t0) {
  const $ = _c(7);
  const {
    onClick,
    ref
  } = t0;
  const intl = useLocaleReceiver("Button");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={faEye} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== intl) {
    t2 = intl.formatMessage({
      id: "viewVariantText"
    });
    $[1] = intl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClick || $[4] !== ref || $[5] !== t2) {
    t3 = <TextButton ref={ref} onClick={onClick} icon={t1} text={t2} />;
    $[3] = onClick;
    $[4] = ref;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Icon: [PREFIX] long arrow lest
 * @defaultValue type='link' (Primary color)
 */
export function BackButton(t0) {
  const $ = _c(17);
  let T0;
  let children;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      children: t5,
      className,
      ref,
      ...buttonProps
    } = t0;
    children = t5;
    T0 = Button;
    t1 = ref;
    t2 = "link";
    if ($[7] !== className) {
      t3 = cx("u-font-weight-semi", "u-color--neutral-600", className);
      $[7] = className;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t4 = filterUndefined(buttonProps);
    $[0] = t0;
    $[1] = T0;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    T0 = $[1];
    children = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={faLongArrowLeft} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== T0 || $[11] !== children || $[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t6 = <T0 ref={t1} type={t2} className={t3} {...t4} icon={t5}>{children}</T0>;
    $[10] = T0;
    $[11] = children;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}

/**
 * Icon: [SUFFIX] caret down
 */
export function CaretButton(t0) {
  const $ = _c(13);
  let T0;
  let children;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    const {
      loading,
      children: t4,
      ref,
      ...buttonProps
    } = t0;
    children = t4;
    T0 = Button;
    t1 = ref;
    t2 = loading;
    t3 = filterUndefined(buttonProps);
    $[0] = t0;
    $[1] = T0;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    T0 = $[1];
    children = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Icon icon={faCaretDown} style={{
      marginLeft: spacing.small
    }} />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== T0 || $[8] !== children || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t5 = <T0 ref={t1} loading={t2} {...t3}>{children}{t4}</T0>;
    $[7] = T0;
    $[8] = children;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}

/**
 * Icon: [SUFFIX] long arrow right
 * @defaultValue type='link' (Primary color)
 */
export function GoToButton(t0) {
  const $ = _c(20);
  let T0;
  let children;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    const {
      children: t6,
      ref,
      ...buttonProps
    } = t0;
    children = t6;
    T0 = Button;
    t1 = ref;
    t2 = "link";
    t3 = "small";
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        paddingLeft: 0
      };
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t5 = filterUndefined(buttonProps);
    $[0] = t0;
    $[1] = T0;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    T0 = $[1];
    children = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={faLongArrowRight} style={{
      marginLeft: spacing.small
    }} />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== children) {
    t7 = <span className="u-font-weight-semi">{children}{t6}</span>;
    $[10] = children;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== T0 || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t7) {
    t8 = <T0 ref={t1} type={t2} size={t3} style={t4} {...t5}>{t7}</T0>;
    $[12] = T0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}

/**
 * Icon: [PREFIX] plus circle
 * @defaultValue type='link' (Primary color)
 */
export function PlusButton(t0) {
  const $ = _c(15);
  let T0;
  let children;
  let style;
  let t1;
  let t2;
  if ($[0] !== t0) {
    const {
      children: t3,
      style: t4,
      ref,
      ...buttonProps
    } = t0;
    children = t3;
    style = t4;
    T0 = Button;
    t1 = ref;
    t2 = filterUndefined(buttonProps);
    $[0] = t0;
    $[1] = T0;
    $[2] = children;
    $[3] = style;
    $[4] = t1;
    $[5] = t2;
  } else {
    T0 = $[1];
    children = $[2];
    style = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== style) {
    t3 = {
      ...style,
      padding: 0
    };
    $[6] = style;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Icon icon={faPlusCircle} />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== T0 || $[10] !== children || $[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t5 = <T0 ref={t1} {...t2} size="small" type="link" style={t3} icon={t4}>{children}</T0>;
    $[9] = T0;
    $[10] = children;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}